/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
// Third-party
import { css, Global } from '@emotion/react'
import {
  Modal,
  Form,
  Select,
  Input,
  Switch,
  InputNumber,
  TimePicker,
  DatePicker,
  Checkbox,
  message,
} from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import useConstraintTypes from '../hooks/useConstraintTypes'
import {
  CreateConstraintData,
  SchedulerConstraint,
} from '../hooks/useSchedulerConstraints'
import { darkPortalStyles, selectAllItemStyles } from '../styles/shared'

const { Option, OptGroup } = Select
const { TextArea } = Input

/**
 * Division type from parent component
 */
interface Division {
  uid: string
  name: string
}

/**
 * Team type from parent component
 */
interface Team {
  uid: string
  name: string
  divisionUid: string
}

/**
 * Venue type from parent component
 */
interface Venue {
  uid: string
  name: string
}

/**
 * Facility type from parent component
 */
interface Facility {
  uid: string
  name: string
}

/**
 * Data for updating a constraint
 */
export interface UpdateConstraintData {
  parameters?: Record<string, unknown>
  description?: string
  is_hard?: boolean
  is_enabled?: boolean
  division_uids?: string[] | null
  team_uids?: string[] | null
}

/**
 * Props for AddConstraintModal
 */
interface AddConstraintModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: CreateConstraintData) => Promise<void>
  onUpdate?: (constraintId: string, data: UpdateConstraintData) => Promise<void>
  divisions?: Division[]
  teams?: Team[]
  venues?: Venue[]
  facilities?: Facility[]
  defaultIsHard?: boolean
  editingConstraint?: SchedulerConstraint | null
}

/**
 * Days of week options (0=Sunday, 6=Saturday)
 */
const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

/**
 * Modal styles
 */
const modalStyles = css`
  .ant-modal-content {
    background-color: ${colors.SECONDARY_500};
    border: 1px solid ${colors.SECONDARY_200};
  }

  .ant-modal-header {
    background-color: ${colors.SECONDARY_500};
    border-bottom: 1px solid ${colors.SECONDARY_200};
  }

  .ant-modal-title {
    color: ${colors.WHITE};
  }

  .ant-modal-close-x {
    color: ${colors.NEUTRAL_100};
  }

  .ant-form-item-label > label {
    color: ${colors.NEUTRAL_50};
  }

  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-picker {
    background-color: ${colors.SECONDARY_400} !important;
    border-color: ${colors.SECONDARY_200} !important;
    color: ${colors.WHITE} !important;
  }

  .ant-select-arrow,
  .ant-picker-suffix {
    color: ${colors.NEUTRAL_100};
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: ${colors.WHITE};
  }

  .ant-checkbox-wrapper {
    color: ${colors.NEUTRAL_50};
  }
`

const formItemStyles = css`
  margin-bottom: 16px;
`

const HardSoftToggle = ({
  value,
  onChange,
}: {
  value?: boolean
  onChange?: (val: boolean) => void
}) => {
  const isHard = !!value
  return (
    <div
      css={css`
        display: inline-flex;
        border-radius: 4px;
        overflow: hidden;
      `}
    >
      <button
        type='button'
        onClick={() => onChange?.(false)}
        css={css`
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1px;
          border: none;
          cursor: pointer;
          color: ${colors.NEUTRAL_0};
          background: ${!isHard ? colors.SUCCESS_300 : colors.NEUTRAL_600};
          transition: background 0.15s;
        `}
      >
        SOFT
      </button>
      <button
        type='button'
        onClick={() => onChange?.(true)}
        css={css`
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 1px;
          border: none;
          cursor: pointer;
          color: ${colors.NEUTRAL_0};
          background: ${isHard ? colors.ERROR_300 : colors.NEUTRAL_600};
          transition: background 0.15s;
        `}
      >
        HARD
      </button>
    </div>
  )
}

const sectionTitleStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.NEUTRAL_100};
  margin: 16px 0 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`

export const AddConstraintModal: React.FC<AddConstraintModalProps> = ({
  open,
  onClose,
  onSubmit,
  onUpdate,
  divisions = [],
  teams = [],
  venues = [],
  facilities = [],
  defaultIsHard,
  editingConstraint,
}) => {
  const isEditMode = !!editingConstraint
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(null)
  const pendingFormValuesRef = useRef<Record<string, unknown> | null>(null)
  const { constraintTypes, loading: typesLoading } = useConstraintTypes()

  // Refs to close dropdowns after "Select All"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const divisionsSelectRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const teamsSelectRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const facilitiesSelectRef = useRef<any>(null)

  const selectedType = constraintTypes.find((t) => t.code === selectedTypeCode)

  // Watch selected divisions to filter teams
  const selectedDivisionUids: string[] | undefined = Form.useWatch(
    'division_uids',
    form
  )

  // Filter teams based on selected divisions
  const filteredTeams = useMemo(() => {
    if (!selectedDivisionUids || selectedDivisionUids.length === 0) {
      // No divisions selected - show all teams
      return teams
    }
    // Filter teams to only those belonging to selected divisions
    return teams.filter((t) => selectedDivisionUids.includes(t.divisionUid))
  }, [teams, selectedDivisionUids])

  // Group filtered teams by division for display
  const teamsByDivision = useMemo(() => {
    const grouped: { division: Division; teams: Team[] }[] = []
    const divisionMap = new Map<string, Team[]>()

    // Group teams by divisionUid
    filteredTeams.forEach((team) => {
      const existing = divisionMap.get(team.divisionUid) || []
      existing.push(team)
      divisionMap.set(team.divisionUid, existing)
    })

    // Build grouped array with division info
    divisionMap.forEach((divisionTeams, divisionUid) => {
      const division = divisions.find((d) => d.uid === divisionUid)
      if (division) {
        grouped.push({ division, teams: divisionTeams })
      }
    })

    // Sort by division name
    grouped.sort((a, b) => a.division.name.localeCompare(b.division.name))

    return grouped
  }, [filteredTeams, divisions])

  // Clear invalid team selections when divisions change
  useEffect(() => {
    if (!selectedDivisionUids || selectedDivisionUids.length === 0) {
      // No divisions selected - keep all teams
      return
    }
    const currentTeamUids: string[] | undefined =
      form.getFieldValue('team_uids')
    if (currentTeamUids && currentTeamUids.length > 0) {
      // Filter out teams that no longer belong to selected divisions
      const validTeamUids = currentTeamUids.filter((teamUid) =>
        filteredTeams.some((t) => t.uid === teamUid)
      )
      if (validTeamUids.length !== currentTeamUids.length) {
        form.setFieldsValue({ team_uids: validTeamUids })
      }
    }
  }, [selectedDivisionUids, filteredTeams, form])

  // Reset form when modal opens/closes or pre-populate in edit mode
  useEffect(() => {
    if (open) {
      form.resetFields()
      if (editingConstraint) {
        // Pre-populate form with existing values for edit mode
        setSelectedTypeCode(editingConstraint.type.code)
        const params = editingConstraint.parameters as Record<string, unknown>

        // Set common (always-mounted) fields immediately
        const commonValues: Record<string, unknown> = {
          constraint_type_code: editingConstraint.type.code,
          description: editingConstraint.description || '',
          is_hard: editingConstraint.is_hard,
          is_enabled: editingConstraint.is_enabled,
          division_uids: editingConstraint.division_uids || [],
          team_uids: editingConstraint.team_uids || [],
        }
        form.setFieldsValue(commonValues)

        // Defer parameter-specific fields until after the type-specific
        // Form.Items have mounted (triggered by setSelectedTypeCode re-render)
        const paramValues: Record<string, unknown> = {}
        switch (editingConstraint.type.code) {
          case 'no_back_to_back':
            paramValues.min_hours_between = params.min_hours_between || 24
            break
          case 'min_rest_days':
            paramValues.min_days = params.min_days || 1
            break
          case 'bye_distribution':
            paramValues.max_consecutive_byes = params.max_consecutive_byes || 2
            break
          case 'home_away_balance':
            paramValues.target_home_percentage =
              params.target_home_percentage || 50
            break
          case 'preferred_day':
            paramValues.preferred_days = params.days || []
            break
          case 'time_window':
            if (params.start_time) {
              paramValues.start_time = dayjs(
                params.start_time as string,
                'HH:mm'
              )
            }
            if (params.end_time) {
              paramValues.end_time = dayjs(params.end_time as string, 'HH:mm')
            }
            paramValues.days = params.days || []
            break
          case 'preferred_time':
            if (params.start_time) {
              paramValues.pref_start_time = dayjs(
                params.start_time as string,
                'HH:mm'
              )
            }
            if (params.end_time) {
              paramValues.pref_end_time = dayjs(
                params.end_time as string,
                'HH:mm'
              )
            }
            break
          case 'team_bye_period':
            if (params.start_date) {
              paramValues.start_date = dayjs(params.start_date as string)
            }
            if (params.end_date) {
              paramValues.end_date = dayjs(params.end_date as string)
            }
            break
          case 'venue_capacity':
            // Support both old (facility_uid) and new (facility_uids) data format
            paramValues.facility_uids =
              params.facility_uids ||
              (params.facility_uid ? [params.facility_uid] : [])
            paramValues.max_concurrent_games = params.max_concurrent_games
            break
          case 'max_games_per_team_per_day':
            paramValues.max_games = params.max_games || 2
            break
          case 'games_per_team_per_week':
            paramValues.games_per_week = params.games_per_week || 1
            break
        }
        pendingFormValuesRef.current = paramValues
      } else {
        pendingFormValuesRef.current = null
        setSelectedTypeCode(null)
      }
    }
  }, [open, form, editingConstraint])

  // Apply deferred parameter values after type-specific Form.Items have mounted
  useEffect(() => {
    if (pendingFormValuesRef.current && selectedTypeCode) {
      // Use requestAnimationFrame to ensure Form.Items are rendered
      const raf = requestAnimationFrame(() => {
        if (pendingFormValuesRef.current) {
          form.setFieldsValue(pendingFormValuesRef.current)
          pendingFormValuesRef.current = null
        }
      })
      return () => cancelAnimationFrame(raf)
    }
  }, [selectedTypeCode, form])

  // Set default is_hard based on selected type (only in add mode)
  useEffect(() => {
    if (selectedType && !editingConstraint) {
      const defaultHard =
        defaultIsHard !== undefined
          ? defaultIsHard
          : selectedType.is_hard_default
      form.setFieldsValue({ is_hard: defaultHard })
    }
  }, [selectedType, defaultIsHard, form, editingConstraint])

  const handleTypeChange = useCallback((value: string) => {
    setSelectedTypeCode(value)
  }, [])

  // Handle "All Divisions" toggle for divisions
  const handleSelectAllDivisions = useCallback(() => {
    const allDivisionUids = divisions.map((d) => d.uid)
    const currentUids: string[] = form.getFieldValue('division_uids') || []
    const allSelected = currentUids.length === allDivisionUids.length
    form.setFieldsValue({ division_uids: allSelected ? [] : allDivisionUids })
    // Close the dropdown
    divisionsSelectRef.current?.blur()
  }, [divisions, form])

  // Handle "All Teams" toggle for teams
  const handleSelectAllTeams = useCallback(() => {
    const allTeamUids = filteredTeams.map((t) => t.uid)
    const currentUids: string[] = form.getFieldValue('team_uids') || []
    const allSelected = currentUids.length === allTeamUids.length
    form.setFieldsValue({ team_uids: allSelected ? [] : allTeamUids })
    // Close the dropdown
    teamsSelectRef.current?.blur()
  }, [filteredTeams, form])

  // Handle "All Facilities" toggle for facilities
  const handleSelectAllFacilities = useCallback(() => {
    const allFacilityUids = facilities.map((f) => f.uid)
    const currentUids: string[] = form.getFieldValue('facility_uids') || []
    const allSelected = currentUids.length === allFacilityUids.length
    form.setFieldsValue({ facility_uids: allSelected ? [] : allFacilityUids })
    // Close the dropdown
    facilitiesSelectRef.current?.blur()
  }, [facilities, form])

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()
      setSubmitting(true)

      // Build the constraint data
      // In edit mode the type dropdown is hidden, so use selectedTypeCode state
      const effectiveTypeCode = selectedTypeCode || values.constraint_type_code
      // Use constraint type name as default description when none provided
      const constraintData: CreateConstraintData = {
        constraint_type_code: effectiveTypeCode,
        parameters: {},
        description: values.description || selectedType?.name || undefined,
        is_hard: values.is_hard,
        is_enabled: values.is_enabled !== false,
        division_uids:
          values.division_uids?.length > 0 ? values.division_uids : null,
        team_uids: values.team_uids?.length > 0 ? values.team_uids : null,
      }

      // Build parameters based on constraint type
      switch (effectiveTypeCode) {
        case 'venue_blackout':
          constraintData.parameters = {
            dates:
              values.dates?.map((d: Dayjs) => d.format('YYYY-MM-DD')) || [],
          }
          if (values.venue_uid) {
            constraintData.parameters.venue_uid = values.venue_uid
          }
          if (values.facility_uid) {
            constraintData.parameters.facility_uid = values.facility_uid
          }
          break

        case 'venue_capacity':
          constraintData.parameters = {
            facility_uids: values.facility_uids,
            max_concurrent_games: values.max_concurrent_games,
          }
          break

        case 'time_window':
          constraintData.parameters = {
            start_time: values.start_time?.format('HH:mm'),
            end_time: values.end_time?.format('HH:mm'),
          }
          if (values.days?.length > 0) {
            constraintData.parameters.days = values.days
          }
          break

        case 'team_bye_period':
          constraintData.parameters = {
            start_date: values.start_date?.format('YYYY-MM-DD'),
            end_date: values.end_date?.format('YYYY-MM-DD'),
          }
          break

        case 'no_back_to_back':
          constraintData.parameters = {
            min_hours_between: values.min_hours_between,
          }
          break

        case 'min_rest_days':
          constraintData.parameters = {
            min_days: values.min_days,
          }
          break

        case 'bye_distribution':
          constraintData.parameters = {
            max_consecutive_byes: values.max_consecutive_byes,
          }
          break

        case 'home_away_balance':
          constraintData.parameters = {
            target_home_percentage: values.target_home_percentage || 50,
          }
          break

        case 'preferred_day':
          constraintData.parameters = {
            days: values.preferred_days || [],
          }
          break

        case 'preferred_time':
          constraintData.parameters = {
            start_time: values.pref_start_time?.format('HH:mm'),
            end_time: values.pref_end_time?.format('HH:mm'),
          }
          break

        case 'max_games_per_team_per_day':
          constraintData.parameters = {
            max_games: values.max_games,
          }
          break

        case 'games_per_team_per_week':
          constraintData.parameters = {
            games_per_week: values.games_per_week,
          }
          break
      }

      if (isEditMode && editingConstraint && onUpdate) {
        // Fallback: if parameters ended up empty (e.g. due to form timing),
        // use the existing constraint's parameters to avoid sending {}
        // Check for defined values (undefined values are dropped by JSON.stringify)
        const hasParams =
          constraintData.parameters &&
          Object.values(constraintData.parameters).some(
            (v) => v !== undefined && v !== null
          )
        const finalParameters = hasParams
          ? constraintData.parameters
          : (editingConstraint.parameters as Record<string, unknown>)

        // Update existing constraint
        await onUpdate(editingConstraint.id, {
          parameters: finalParameters,
          description: constraintData.description,
          is_hard: constraintData.is_hard,
          is_enabled: constraintData.is_enabled,
          division_uids: constraintData.division_uids,
          team_uids: constraintData.team_uids,
        })
        message.success('Constraint updated successfully')
      } else {
        // Create new constraint
        await onSubmit(constraintData)
        message.success('Constraint added successfully')
      }
      onClose()
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message)
      } else {
        message.error(
          isEditMode
            ? 'Failed to update constraint'
            : 'Failed to add constraint'
        )
      }
    } finally {
      setSubmitting(false)
    }
  }, [
    form,
    onSubmit,
    onUpdate,
    onClose,
    isEditMode,
    editingConstraint,
    selectedTypeCode,
    selectedType,
  ])

  const renderParameterFields = () => {
    if (!selectedTypeCode) {
      return null
    }

    switch (selectedTypeCode) {
      case 'venue_blackout':
        return (
          <>
            <Form.Item
              name='venue_uid'
              label='Venue (optional)'
              css={formItemStyles}
            >
              <Select
                placeholder='All venues'
                allowClear
                showSearch
                optionFilterProp='children'
                popupClassName='dark-select-popup'
              >
                {venues.map((v) => (
                  <Option key={v.uid} value={v.uid}>
                    {v.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='facility_uid'
              label='Facility (optional)'
              css={formItemStyles}
            >
              <Select
                placeholder='All facilities'
                allowClear
                showSearch
                optionFilterProp='children'
                popupClassName='dark-select-popup'
              >
                {facilities.map((f) => (
                  <Option key={f.uid} value={f.uid}>
                    {f.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='dates'
              label='Blackout Dates'
              rules={[
                { required: true, message: 'At least one date is required' },
              ]}
              css={formItemStyles}
            >
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD'
              />
            </Form.Item>
          </>
        )

      case 'venue_capacity':
        return (
          <>
            <Form.Item
              name='facility_uids'
              label='Facilities'
              rules={[
                {
                  required: true,
                  message: 'At least one facility is required',
                },
              ]}
              css={formItemStyles}
            >
              <Select
                ref={facilitiesSelectRef}
                mode='multiple'
                placeholder='Select facilities'
                allowClear
                showSearch
                optionFilterProp='children'
                popupClassName='dark-select-popup'
                dropdownRender={(menu) => (
                  <>
                    <div
                      css={selectAllItemStyles}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleSelectAllFacilities}
                    >
                      All Facilities
                    </div>
                    {menu}
                  </>
                )}
              >
                {facilities.map((f) => (
                  <Option key={f.uid} value={f.uid}>
                    {f.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name='max_concurrent_games'
              label='Max Concurrent Games'
              rules={[{ required: true, message: 'Max games is required' }]}
              css={formItemStyles}
            >
              <InputNumber min={1} max={20} style={{ width: '100%' }} />
            </Form.Item>
          </>
        )

      case 'time_window':
        return (
          <>
            <Form.Item
              name='start_time'
              label='Start Time'
              rules={[{ required: true, message: 'Start time is required' }]}
              css={formItemStyles}
            >
              <TimePicker format='HH:mm' style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name='end_time'
              label='End Time'
              rules={[{ required: true, message: 'End time is required' }]}
              css={formItemStyles}
            >
              <TimePicker format='HH:mm' style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name='days' label='Days (optional)' css={formItemStyles}>
              <Checkbox.Group options={DAYS_OF_WEEK} />
            </Form.Item>
          </>
        )

      case 'team_bye_period':
        return (
          <>
            <Form.Item
              name='start_date'
              label='Start Date'
              rules={[{ required: true, message: 'Start date is required' }]}
              css={formItemStyles}
            >
              <DatePicker format='YYYY-MM-DD' style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name='end_date'
              label='End Date'
              rules={[{ required: true, message: 'End date is required' }]}
              css={formItemStyles}
            >
              <DatePicker format='YYYY-MM-DD' style={{ width: '100%' }} />
            </Form.Item>
          </>
        )

      case 'no_back_to_back':
        return (
          <Form.Item
            name='min_hours_between'
            label='Minimum Hours Between Games'
            rules={[{ required: true, message: 'Min hours is required' }]}
            initialValue={24}
            css={formItemStyles}
          >
            <InputNumber min={1} max={168} style={{ width: '100%' }} />
          </Form.Item>
        )

      case 'min_rest_days':
        return (
          <Form.Item
            name='min_days'
            label='Minimum Rest Days'
            rules={[{ required: true, message: 'Min days is required' }]}
            initialValue={1}
            css={formItemStyles}
          >
            <InputNumber min={1} max={14} style={{ width: '100%' }} />
          </Form.Item>
        )

      case 'bye_distribution':
        return (
          <Form.Item
            name='max_consecutive_byes'
            label='Max Consecutive Byes'
            rules={[{ required: true, message: 'Max byes is required' }]}
            initialValue={2}
            css={formItemStyles}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        )

      case 'home_away_balance':
        return (
          <Form.Item
            name='target_home_percentage'
            label='Target Home Percentage'
            initialValue={50}
            css={formItemStyles}
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              addonAfter='%'
            />
          </Form.Item>
        )

      case 'preferred_day':
        return (
          <Form.Item
            name='preferred_days'
            label='Preferred Days'
            rules={[
              { required: true, message: 'At least one day is required' },
            ]}
            css={formItemStyles}
          >
            <Checkbox.Group options={DAYS_OF_WEEK} />
          </Form.Item>
        )

      case 'preferred_time':
        return (
          <>
            <Form.Item
              name='pref_start_time'
              label='Preferred Start Time'
              rules={[{ required: true, message: 'Start time is required' }]}
              css={formItemStyles}
            >
              <TimePicker format='HH:mm' style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name='pref_end_time'
              label='Preferred End Time'
              rules={[{ required: true, message: 'End time is required' }]}
              css={formItemStyles}
            >
              <TimePicker format='HH:mm' style={{ width: '100%' }} />
            </Form.Item>
          </>
        )

      case 'max_games_per_team_per_day':
        return (
          <Form.Item
            name='max_games'
            label='Max Games Per Team Per Day'
            rules={[
              {
                required: true,
                message: 'Max games per team per day is required',
              },
            ]}
            initialValue={2}
            css={formItemStyles}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
        )

      case 'games_per_team_per_week':
        return (
          <Form.Item
            name='games_per_week'
            label='Games Per Week'
            rules={[
              {
                required: true,
                message: 'Games per week is required',
              },
            ]}
            initialValue={1}
            css={formItemStyles}
          >
            <InputNumber min={1} max={7} style={{ width: '100%' }} />
          </Form.Item>
        )

      default:
        return null
    }
  }

  return (
    <>
      <Global styles={darkPortalStyles} />
      <Modal
        title={
          isEditMode
            ? `Edit Constraint - ${editingConstraint?.type?.name || ''}`
            : 'Add Constraint'
        }
        open={open}
        onCancel={onClose}
        onOk={handleSubmit}
        okText={isEditMode ? 'Save Changes' : 'Add Constraint'}
        cancelText='Cancel'
        confirmLoading={submitting}
        width={600}
        css={modalStyles}
        okButtonProps={{
          style: {
            backgroundColor: colors.PRIMARY_300,
            borderColor: colors.PRIMARY_300,
          },
        }}
      >
        <Form
          form={form}
          layout='vertical'
          initialValues={{ is_hard: false, is_enabled: true }}
        >
          {/* Constraint Type — hidden in edit mode (type shown in title) */}
          {!isEditMode && (
            <Form.Item
              name='constraint_type_code'
              label='Constraint Type'
              rules={[
                { required: true, message: 'Please select a constraint type' },
              ]}
              css={formItemStyles}
            >
              <Select
                placeholder='Select constraint type'
                loading={typesLoading}
                onChange={handleTypeChange}
                showSearch
                optionFilterProp='children'
                popupClassName='dark-select-popup'
              >
                {constraintTypes.map((type) => (
                  <Option key={type.code} value={type.code}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Description */}
          <Form.Item
            name='description'
            label='Description (optional)'
            css={formItemStyles}
          >
            <TextArea
              rows={2}
              placeholder='Add a description for this constraint'
            />
          </Form.Item>

          {/* Hard/Soft Toggle */}
          <Form.Item
            name='is_hard'
            label='Constraint Type'
            css={formItemStyles}
          >
            <HardSoftToggle />
          </Form.Item>

          {/* Enabled Toggle */}
          <Form.Item
            name='is_enabled'
            label='Enabled'
            valuePropName='checked'
            css={formItemStyles}
          >
            <Switch defaultChecked />
          </Form.Item>

          {/* Scope: Divisions and Teams - hide for venue-related constraints */}
          {selectedTypeCode !== 'venue_capacity' &&
            selectedTypeCode !== 'venue_blackout' && (
              <>
                <div css={sectionTitleStyles}>Scope</div>
                <Form.Item
                  name='division_uids'
                  label='Divisions'
                  rules={[
                    {
                      required: true,
                      message: 'Please select at least one division',
                      type: 'array',
                    },
                  ]}
                  css={formItemStyles}
                >
                  <Select
                    ref={divisionsSelectRef}
                    mode='multiple'
                    placeholder='Select divisions'
                    allowClear
                    showSearch
                    optionFilterProp='children'
                    popupClassName='dark-select-popup'
                    dropdownRender={(menu) => (
                      <>
                        <div
                          css={selectAllItemStyles}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleSelectAllDivisions}
                        >
                          All Divisions
                        </div>
                        {menu}
                      </>
                    )}
                  >
                    {divisions.map((d) => (
                      <Option key={d.uid} value={d.uid}>
                        {d.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                {/* Scope: Teams */}
                <Form.Item
                  name='team_uids'
                  label='Teams'
                  rules={[
                    {
                      required: true,
                      message: 'Please select at least one team',
                      type: 'array',
                    },
                  ]}
                  css={formItemStyles}
                >
                  <Select
                    ref={teamsSelectRef}
                    mode='multiple'
                    placeholder='Select teams'
                    allowClear
                    showSearch
                    optionFilterProp='children'
                    popupClassName='dark-select-popup'
                    dropdownRender={(menu) => (
                      <>
                        <div
                          css={selectAllItemStyles}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleSelectAllTeams}
                        >
                          All Teams
                        </div>
                        {menu}
                      </>
                    )}
                  >
                    {teamsByDivision.map((group) => (
                      <OptGroup
                        key={group.division.uid}
                        label={group.division.name}
                      >
                        {group.teams.map((t) => (
                          <Option key={t.uid} value={t.uid}>
                            {t.name}
                          </Option>
                        ))}
                      </OptGroup>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

          {/* Dynamic Parameter Fields */}
          {selectedTypeCode && (
            <>
              <div css={sectionTitleStyles}>Parameters</div>
              {renderParameterFields()}
            </>
          )}
        </Form>
      </Modal>
    </>
  )
}

export default AddConstraintModal
